import type { ChecklistDocument, DocumentState, EstateRow } from '@acasa/shared'
import { Fragment, useState } from 'react'
import { EstateDetailPanel } from './estate-detail-panel'
import { ATTRIBUTE_COLUMNS, type TableState } from './use-table-state'

/** Short German date; the full timestamp goes in the title attribute. */
function formatDate(iso: string | null) {
  if (!iso) return null
  return new Date(iso).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  })
}

const STATE_TITLE: Record<DocumentState, string> = {
  done: 'vorhanden',
  open: 'offen',
  not_applicable: 'nicht erforderlich',
}

/**
 * One matrix cell.
 *
 * Encoding is deliberate: ~80% of cells are open, so "missing" is the quiet
 * default (an empty well) and "done" is the solid mark. Reading a column then
 * becomes a density judgement rather than a counting exercise.
 */
function MatrixCell({
  step,
  compact,
}: {
  step: { state: DocumentState; note: string | null } | undefined
  compact: boolean
}) {
  const state = step?.state ?? 'open'
  const size = compact ? 'size-3' : 'size-3.5'

  return (
    <td
      className="border-line/60 border-l p-0 text-center align-middle"
      title={step?.note ? `${STATE_TITLE[state]} — ${step.note}` : STATE_TITLE[state]}
    >
      <div className="flex items-center justify-center">
        {state === 'done' && <span className={`${size} bg-done rounded-[2px]`} />}
        {state === 'open' && (
          <span
            className={`${size} bg-open rounded-[2px] ${step?.note ? 'ring-brand/50 ring-1' : ''}`}
          />
        )}
        {state === 'not_applicable' && (
          <span className={`${size} hatch border-na rounded-[2px] border`} />
        )}
      </div>
    </td>
  )
}

/**
 * Completeness = everything that no longer needs chasing.
 *
 * A document marked "Nicht zutreffend"/"Nicht vorhanden" is RESOLVED, not
 * outstanding, so it fills the bar too — just in a lighter tone. Drawing it as
 * empty made objects with one or two genuinely open documents look barely
 * started.
 */
function ProgressBar({ estate }: { estate: EstateRow }) {
  const donePct = estate.totalCount ? (estate.doneCount / estate.totalCount) * 100 : 0
  const naPct = estate.totalCount ? (estate.notApplicableCount / estate.totalCount) * 100 : 0
  const resolved = estate.doneCount + estate.notApplicableCount

  return (
    <div
      className="flex items-center gap-2"
      title={`${estate.doneCount} vorhanden · ${estate.notApplicableCount} nicht erforderlich · ${estate.openCount} offen`}
    >
      <div className="bg-open h-1.5 w-16 shrink-0 overflow-hidden rounded-full">
        <div className="flex h-full">
          <div className="bg-done h-full" style={{ width: `${donePct}%` }} />
          <div className="bg-done/35 h-full" style={{ width: `${naPct}%` }} />
        </div>
      </div>
      <span
        className={`tnum font-mono text-[11px] ${
          estate.openCount === 0 ? 'text-done' : 'text-muted'
        }`}
      >
        {resolved}/{estate.totalCount}
      </span>
    </div>
  )
}

function SortHeader({
  label,
  sortKey,
  state,
  onSort,
  className = '',
  align = 'left',
}: {
  label: string
  sortKey: string
  state: TableState
  onSort: (key: string) => void
  className?: string
  align?: 'left' | 'right'
}) {
  const active = state.sortKey === sortKey

  return (
    <th className={`bg-sand-soft border-line border-b px-2 py-2 font-medium ${className}`}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`flex w-full items-center gap-1 text-[11px] tracking-wide uppercase transition-colors ${
          align === 'right' ? 'justify-end' : ''
        } ${active ? 'text-ink' : 'text-faint hover:text-muted'}`}
      >
        {label}
        <span className={active ? 'opacity-100' : 'opacity-0'}>
          {state.sortDirection === 'asc' ? '↑' : '↓'}
        </span>
      </button>
    </th>
  )
}

export function EstateTable({
  estates,
  documents,
  state,
  onSort,
}: {
  estates: EstateRow[]
  documents: ChecklistDocument[]
  state: TableState
  onSort: (key: string) => void
}) {
  const [expanded, setExpanded] = useState<number | null>(null)

  const visibleDocs =
    state.visibleDocuments.length === 0
      ? documents
      : documents.filter((document) => state.visibleDocuments.includes(document.key))

  const attributes = ATTRIBUTE_COLUMNS.filter((column) =>
    state.visibleAttributes.includes(column.key),
  )

  const rowPadding = state.compact ? 'py-1' : 'py-2'
  const columnCount = 1 + attributes.length + (state.showMatrix ? visibleDocs.length : 0)

  if (estates.length === 0) {
    return (
      <div className="text-muted flex-1 px-4 py-16 text-center text-sm">
        Keine Objekte für diese Filter.
      </div>
    )
  }

  return (
    <div className="min-h-0 flex-1 overflow-auto">
      <table className="w-full border-collapse text-xs">
        <thead className="sticky top-0 z-10">
          <tr>
            <th className="bg-sand-soft border-line sticky left-0 z-10 border-r border-b px-3 py-2 text-left">
              <button
                type="button"
                onClick={() => onSort('attr:immoNr')}
                className={`flex items-center gap-1 text-[11px] tracking-wide uppercase ${
                  state.sortKey === 'attr:immoNr' ? 'text-ink' : 'text-faint hover:text-muted'
                }`}
              >
                ImmoNr
                <span className={state.sortKey === 'attr:immoNr' ? 'opacity-100' : 'opacity-0'}>
                  {state.sortDirection === 'asc' ? '↑' : '↓'}
                </span>
              </button>
            </th>

            {attributes.map((column) => (
              <SortHeader
                key={column.key}
                label={column.label}
                sortKey={`attr:${column.key}`}
                state={state}
                onSort={onSort}
                align={column.key === 'open' ? 'right' : 'left'}
                className={column.key === 'title' ? 'min-w-[14rem]' : 'whitespace-nowrap'}
              />
            ))}

            {state.showMatrix &&
              visibleDocs.map((document) => (
                <th
                  key={document.key}
                  className="bg-sand-soft border-line border-b border-l p-0 align-bottom"
                >
                  <button
                    type="button"
                    onClick={() => onSort(`doc:${document.key}`)}
                    title={`${document.label} — ${document.openCount} offen`}
                    className={`font-condensed matrix-head h-40 w-7 overflow-hidden px-1 py-2 text-left text-[11px] text-ellipsis whitespace-nowrap transition-colors ${
                      state.sortKey === `doc:${document.key}`
                        ? 'text-ink bg-brand-soft'
                        : 'text-muted hover:text-ink'
                    }`}
                  >
                    {document.label}
                  </button>
                </th>
              ))}
          </tr>
        </thead>

        <tbody>
          {estates.map((estate, index) => {
            const isExpanded = expanded === estate.estateId
            const toggle = () => setExpanded(isExpanded ? null : estate.estateId)

            // Zebra is computed rather than done with `even:`, because the
            // sticky ImmoNr cell needs the SAME background or it would show a
            // mismatched stripe while the row scrolls underneath it.
            const zebra = isExpanded
              ? 'bg-brand-soft'
              : index % 2 === 1
                ? 'bg-raised'
                : 'bg-surface'

            return (
              <Fragment key={estate.estateId}>
                <tr
                  onClick={toggle}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      toggle()
                    }
                  }}
                  tabIndex={0}
                  aria-expanded={isExpanded}
                  className={`border-line/70 hover:bg-brand-soft/40 cursor-pointer border-b transition-colors ${
                    isExpanded ? 'bg-brand-soft/60' : index % 2 === 1 ? 'bg-raised/60' : ''
                  }`}
                >
                  <td
                    className={`border-line sticky left-0 z-10 border-r px-3 ${rowPadding} ${zebra}`}
                  >
                    <span className="tnum text-ink font-mono text-[11px] font-medium">
                      {estate.externalNumber ?? `#${estate.estateId}`}
                    </span>
                  </td>

                  {attributes.map((column) => (
                    <td
                      key={column.key}
                      className={`px-2 ${rowPadding} ${
                        column.key === 'open' ? 'text-right' : ''
                      } ${column.key === 'title' ? 'max-w-[22rem]' : 'whitespace-nowrap'}`}
                    >
                      {column.key === 'progress' ? (
                        <ProgressBar estate={estate} />
                      ) : column.key === 'createdAt' ||
                        column.key === 'modifiedAt' ||
                        column.key === 'soldAt' ? (
                        <span
                          className="tnum text-muted font-mono text-[11px]"
                          title={
                            estate[column.key]
                              ? new Date(estate[column.key] as string).toLocaleString('de-DE')
                              : undefined
                          }
                        >
                          {formatDate(estate[column.key]) ?? '—'}
                        </span>
                      ) : column.key === 'open' ? (
                        <span
                          className={`tnum font-mono ${
                            estate.openCount === 0 ? 'text-done' : 'text-ink'
                          }`}
                        >
                          {estate.openCount}
                        </span>
                      ) : column.key === 'title' ? (
                        <span className="text-ink block truncate">{estate.title ?? '—'}</span>
                      ) : (
                        <span className="text-muted">
                          {(estate[column.key as keyof EstateRow] as string | null) ?? '—'}
                        </span>
                      )}
                    </td>
                  ))}

                  {state.showMatrix &&
                    visibleDocs.map((document) => (
                      <MatrixCell
                        key={document.key}
                        step={estate.steps[document.key]}
                        compact={state.compact}
                      />
                    ))}
                </tr>

                {isExpanded && (
                  <tr className="bg-brand-soft/30 border-line border-b">
                    <td colSpan={columnCount} className="px-4 py-4">
                      <EstateDetailPanel estate={estate} documents={documents} />
                    </td>
                  </tr>
                )}
              </Fragment>
            )
          })}
        </tbody>

        {state.showMatrix && (
          <tfoot className="sticky bottom-0 z-10">
            <tr className="border-line bg-sand-soft border-t">
              <td
                className="border-line bg-sand-soft text-faint sticky left-0 z-10 border-r px-3 py-1.5 text-[10px] tracking-wider uppercase"
                colSpan={1 + attributes.length}
              >
                offen je Dokument
              </td>
              {visibleDocs.map((document) => (
                <td
                  key={document.key}
                  className="border-line/60 tnum text-muted border-l px-0.5 py-1.5 text-center font-mono text-[10px]"
                  title={`${document.label}: ${document.openCount} offen`}
                >
                  {document.openCount}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  )
}
